import { useMemo, type ReactNode } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useIsAuthenticated } from '@azure/msal-react';
import { createAppTheme } from '../theme';
import { useWhoami } from '../api/whoami';
import { useDocumentBranding } from './useDocumentBranding';

/**
 * Applies the effective customer's branding to the MUI theme (WEB PRD §2.6).
 *
 * Reads `whoami.customer.brandPrimaryColor` / `brandHeaderColor` and rebuilds the
 * theme through the single {@link createAppTheme} factory. The rebuild is keyed
 * on the two color values only, so unrelated `whoami` refetches (language change,
 * role refresh) don't churn the theme — but an **impersonation start/end**, which
 * invalidates `['whoami']` and can change the effective customer, does re-theme
 * the app. That is the behaviour §2.6 requires.
 *
 * <p>Placement: this sits INSIDE `QueryProvider` (it needs the query client) and
 * OUTSIDE `BrowserRouter`, so it also wraps the public magic-link routes. Those
 * routes have no authenticated session, so the `whoami` read is gated on MSAL's
 * `useIsAuthenticated()` — unauthenticated renders simply get the unbranded
 * Polaris theme, which is correct: the public approval page carries its own
 * token-resolved branding via `PublicLayout`.</p>
 *
 * <p>`CssBaseline` lives here rather than in `App` so it can never be mounted
 * against a different theme than the one in effect.</p>
 */
export function BrandedThemeProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { data } = useWhoami({ enabled: isAuthenticated });

  const brandPrimaryColor = data?.customer?.brandPrimaryColor ?? null;
  const brandHeaderColor = data?.customer?.brandHeaderColor ?? null;

  const theme = useMemo(
    () => createAppTheme({ brandPrimaryColor, brandHeaderColor }),
    [brandPrimaryColor, brandHeaderColor],
  );

  // The favicon follows the same customer, so document-level branding has one
  // owner. (`document.title` is deliberately excluded — `ImpersonationBanner`
  // owns it.)
  useDocumentBranding({ faviconUrl: data?.customer?.faviconUrl ?? null });

  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
